/**
 * Picking a pick up and putting it down, with one code path for a mouse and a finger.
 *
 * HTML5 drag-and-drop does two things badly here and one thing not at all. It does not exist on
 * touch, so the phone had to have a separate tap-to-carry mode; its drag image is a snapshot of
 * the element taken at the grab offset, so the thing following the cursor covers the squares it
 * is being dragged over; and it gives no way to say *which* target is under the pointer without
 * a `dragover` handler on every one of them.
 *
 * Pointer events do all three. The card lifts, follows, and lands — the same way, everywhere.
 *
 * The parts that are easy to get wrong are here rather than in the component, because they are
 * the parts with edges: a press that becomes a drag versus one that stays a tap, and working out
 * which tile is under a point when the thing under the point is the floating card itself.
 */

/**
 * How far a pointer must travel before a press becomes a drag.
 *
 * Not zero, or every tap is a one-pixel drag and the tap-to-carry path can never fire. Not large,
 * or the card feels stuck to the grid before it lets go. Eight CSS pixels is about the width of a
 * thumb's tremor on a phone and invisible on a mouse.
 */
export const LIFT_PX = 8;

export function shouldLift(dx: number, dy: number, threshold = LIFT_PX): boolean {
  return Math.hypot(dx, dy) >= threshold;
}

/**
 * The round of the pick under a point, or null.
 *
 * `elementFromPoint` returns the TOPMOST element, and while a card is lifted the topmost element
 * at the pointer is the card — so it would resolve to whatever the card is over only by accident.
 * The lifted card is `pointer-events: none` for exactly this reason; this walks up from whatever
 * that leaves to the tile, and reads the round off the tile rather than off any coordinate maths,
 * because the grid's geometry is the browser's business and not this module's.
 */
/**
 * How long a finger must rest before a press becomes a lift.
 *
 * A MOUSE can lift on movement alone — nothing else is competing for a horizontal-ish drag inside
 * a list. A FINGER cannot: the same downward movement is how the page is scrolled, so lifting on
 * movement makes a list you can no longer scroll past. Holding still is the one gesture that means
 * nothing else, which is why every mobile reorder in the world is a long press.
 *
 * 260ms is long enough not to fire while a scroll is starting and short enough not to feel stuck.
 */
export const HOLD_MS = 260;

/** Which gesture a pointer of this kind uses to start a drag. */
export const liftsOnHold = (pointerType: string): boolean => pointerType === 'touch';

export function numberAtPoint(
  doc: Document, x: number, y: number, attr: string,
): number | null {
  const el = doc.elementFromPoint(x, y);
  if (!el) return null;
  const host = (el as Element).closest(`[${attr}]`);
  if (!host) return null;
  const raw = host.getAttribute(attr)?.trim();
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function roundAtPoint(doc: Document, x: number, y: number): number | null {
  const el = doc.elementFromPoint(x, y);
  if (!el) return null;
  const li = (el as Element).closest('[data-round]');
  if (!li) return null;
  const raw = li.getAttribute('data-round')?.trim();
  /*
   * The empty check is not belt-and-braces: `Number('')` is 0, which is finite, so an attribute
   * that is present but blank would resolve to round ZERO — a legal-looking number that no pick
   * has. A test caught it; nothing on screen would have.
   */
  if (!raw) return null;
  const round = Number(raw);
  return Number.isFinite(round) ? round : null;
}

/** Where the lifted card should be drawn, given the pointer and the grab offset within it. */
export function liftAt(
  pointer: { x: number; y: number },
  grab: { x: number; y: number },
): { left: number; top: number } {
  /*
   * The card keeps the offset it was grabbed at — press its bottom-left corner and that corner
   * stays under the finger. Re-centring it on the pointer makes it jump on the first move, which
   * reads as the card being snatched away rather than picked up.
   */
  return { left: pointer.x - grab.x, top: pointer.y - grab.y };
}
